import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Smartphone, ArrowRight, ShieldCheck, CheckCircle2, Store, Lock } from 'lucide-react';
import { useToast } from '../context/ToastContext';

const RegisterPage = () => {
    const [phone, setPhone] = useState('');
    const [storeName, setStoreName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [plan, setPlan] = useState('BÁSICO');

    const checkPhoneExists = async (phoneToCheck: string) => {
        console.log('[DEBUG] Verificando telefone:', phoneToCheck);
        try {
            const res = await fetch(`https://zapshop.app/api/admin/check-phone?phone=${encodeURIComponent(phoneToCheck)}`);
            const data = await res.json();
            console.log('[DEBUG] Resposta check-phone:', data);
            return data.exists;
        } catch (error) {
            console.error('Erro ao verificar telefone:', error);
            return false;
        }
    };

    const handleNextStep = async (e: React.FormEvent) => {
        e.preventDefault();
        if (step === 1) {
            if (!phone) return;
            setLoading(true);
            const exists = await checkPhoneExists(phone);
            setLoading(false);
            if (exists) {
                showToast('Este WhatsApp já está registrado em nossa plataforma.', 'error');
                return;
            }
        }
        if (step === 2 && !storeName) return;
        if (step === 3 && !plan) return;
        setStep(step + 1);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email || !password) return;
        setLoading(true);

        try {
            const res = await fetch('https://zapshop.app/api/admin/create-tenant', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    store_name: storeName,
                    owner_name: storeName + ' Admin',
                    email: email,
                    password: password,
                    phone: phone,
                    plan_type: plan
                })
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.error || 'Erro ao criar conta');
            }

            showToast('Conta criada! Redirecionando para o pagamento...', 'success');

            // Redirecionamento para a nova página de Checkout
            setTimeout(() => {
                navigate(`/checkout?tenantId=${data.tenant_id}`);
            }, 2000);
        } catch (error: any) {
            setLoading(false);
            showToast(error.message || 'Erro ao realizar cadastro. Tente novamente.', 'error');
        }
    };

    const plans = [
        { id: 'BÁSICO', price: '97', description: 'Até 100 produtos', color: 'blue' },
        { id: 'INTERMEDIÁRIO', price: '197', description: 'Até 300 produtos', color: 'emerald' },
        { id: 'PRO', price: '497', description: 'Produtos Ilimitados', color: 'brand' }
    ];

    return (
        <div className="min-h-screen bg-surface-50 flex flex-col items-center justify-center p-6 font-sans">

            {/* Background Decor */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden">
                <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand-200 rounded-full blur-[120px] opacity-20"></div>
                <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-emerald-200 rounded-full blur-[120px] opacity-20"></div>
            </div>

            <div className="w-full max-w-md relative z-10">
                <div className="text-center mb-8 space-y-4">
                    <img src="/logozapshopsaas.png" alt="ZapShop" className="h-16 mx-auto mb-6" />
                    <h1 className="text-3xl font-black text-surface-900 tracking-tight">Comece a vender hoje</h1>
                    <p className="text-surface-500 font-medium">Sua loja no WhatsApp em menos de 2 minutos.</p>
                </div>

                <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-brand-900/10 border border-surface-100 overflow-hidden transform transition-all hover:shadow-brand-900/5">
                    <div className="h-2 bg-surface-100 w-full overflow-hidden flex">
                        <div className={`h-full bg-emerald-500 transition-all duration-500 ${step === 1 ? 'w-1/4' : step === 2 ? 'w-2/4' : step === 3 ? 'w-3/4' : 'w-full'}`}></div>
                    </div>

                    <div className="p-8 md:p-10">
                        {step === 1 ? (
                            <form onSubmit={handleNextStep} className="space-y-6 animate-fade-in">
                                <div className="space-y-2 text-center pb-4">
                                    <div className="w-16 h-16 bg-brand-50 rounded-2xl flex items-center justify-center mx-auto mb-2 text-brand-600">
                                        <Smartphone size={32} />
                                    </div>
                                    <h2 className="text-xl font-black text-surface-900">Qual seu WhatsApp?</h2>
                                    <p className="text-sm text-surface-400 font-medium">Usaremos seu número como chave de acesso.</p>
                                </div>

                                <div className="relative">
                                    <Smartphone className="absolute left-4 top-1/2 -translate-y-1/2 text-surface-300" size={20} />
                                    <input
                                        type="tel"
                                        required
                                        placeholder="(00) 00000-0000"
                                        className="w-full pl-12 pr-4 py-4 rounded-2xl border-2 border-surface-100 outline-none focus:border-brand-500 transition-all font-bold text-lg"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full bg-surface-900 hover:bg-black text-white py-5 rounded-2xl font-black text-sm uppercase tracking-widest transition-all active:scale-95 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                                >
                                    {loading ? (
                                        <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            Continuar <ArrowRight size={18} />
                                        </>
                                    )}
                                </button>
                            </form>
                        ) : step === 2 ? (
                            <form onSubmit={handleNextStep} className="space-y-6 animate-fade-in">
                                <div className="space-y-2 text-center pb-4">
                                    <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-2 text-emerald-600">
                                        <Store size={32} />
                                    </div>
                                    <h2 className="text-xl font-black text-surface-900">Nome da sua Loja</h2>
                                    <p className="text-sm text-surface-400 font-medium">Como seus clientes vão te conhecer?</p>
                                </div>

                                <div className="relative">
                                    <Store className="absolute left-4 top-1/2 -translate-y-1/2 text-surface-300" size={20} />
                                    <input
                                        type="text"
                                        required
                                        placeholder="Ex: Minha Loja Incrível"
                                        className="w-full pl-12 pr-4 py-4 rounded-2xl border-2 border-surface-100 outline-none focus:border-brand-500 transition-all font-bold text-lg"
                                        value={storeName}
                                        onChange={(e) => setStoreName(e.target.value)}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    className="w-full bg-surface-900 hover:bg-black text-white py-5 rounded-2xl font-black text-sm uppercase tracking-widest transition-all active:scale-95 flex items-center justify-center gap-2"
                                >
                                    Escolher Plano <ArrowRight size={18} />
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setStep(1)}
                                    className="w-full text-xs font-black text-surface-400 uppercase tracking-widest hover:text-surface-600 transition-colors"
                                >
                                    Voltar e alterar número
                                </button>
                            </form>
                        ) : step === 3 ? (
                            <form onSubmit={handleNextStep} className="space-y-6 animate-fade-in">
                                <div className="space-y-2 text-center pb-4">
                                    <h2 className="text-xl font-black text-surface-900">Escolha seu Plano</h2>
                                    <p className="text-sm text-surface-400 font-medium">Configurações profissionais para sua marca.</p>
                                </div>

                                <div className="space-y-3">
                                    {plans.map((p) => (
                                        <label
                                            key={p.id}
                                            className={`flex items-center justify-between p-4 rounded-2xl border-2 cursor-pointer transition-all ${plan === p.id ? 'border-emerald-500 bg-emerald-50 shadow-lg' : 'border-surface-100 hover:border-surface-200'}`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="radio"
                                                    name="plan"
                                                    className="w-5 h-5 accent-emerald-500"
                                                    checked={plan === p.id}
                                                    onChange={() => setPlan(p.id)}
                                                />
                                                <div>
                                                    <p className="text-xs font-black uppercase text-surface-900">{p.id}</p>
                                                    <p className="text-[10px] font-bold text-surface-400 uppercase">{p.description}</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-lg font-black text-surface-900">R$ {p.price}</p>
                                                <p className="text-[9px] font-bold text-surface-400 uppercase">/mês</p>
                                            </div>
                                        </label>
                                    ))}
                                </div>

                                <button
                                    type="submit"
                                    className="w-full bg-surface-900 hover:bg-black text-white py-5 rounded-2xl font-black text-sm uppercase tracking-widest transition-all active:scale-95 flex items-center justify-center gap-2"
                                >
                                    Dados de Acesso <ArrowRight size={18} />
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setStep(2)}
                                    className="w-full text-xs font-black text-surface-400 uppercase tracking-widest hover:text-surface-600 transition-colors"
                                >
                                    Voltar
                                </button>
                            </form>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-6 animate-fade-in">
                                <div className="space-y-2 text-center pb-4">
                                    <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-2 text-blue-600">
                                        <Lock size={32} />
                                    </div>
                                    <h2 className="text-xl font-black text-surface-900">Dados da Conta</h2>
                                    <p className="text-sm text-surface-400 font-medium">Como você acessará seu painel.</p>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="text-xs font-bold text-surface-400 uppercase tracking-wider ml-1">E-mail Corporativo</label>
                                        <input
                                            type="email"
                                            required
                                            placeholder="seu@email.com"
                                            className="w-full px-4 py-4 rounded-2xl border-2 border-surface-100 outline-none focus:border-brand-500 transition-all font-bold"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold text-surface-400 uppercase tracking-wider ml-1">Crie uma Senha</label>
                                        <input
                                            type="password"
                                            required
                                            placeholder="••••••••"
                                            className="w-full px-4 py-4 rounded-2xl border-2 border-surface-100 outline-none focus:border-brand-500 transition-all font-bold"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading || !email || !password}
                                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-5 rounded-2xl font-black text-sm uppercase tracking-widest shadow-2xl shadow-emerald-500/20 transition-all active:scale-95 flex items-center justify-center gap-2"
                                >
                                    {loading ? 'Processando...' : (
                                        <>
                                            Ativar Agora <CheckCircle2 size={18} />
                                        </>
                                    )}
                                </button>

                                <button type="button" onClick={() => setStep(3)} className="w-full text-xs font-black text-surface-400 uppercase tracking-widest hover:text-surface-600 transition-colors">Voltar</button>
                            </form>
                        )}
                    </div>
                </div>

                <div className="mt-8 text-center space-y-4">
                    <p className="text-xs text-surface-400 font-bold uppercase tracking-widest flex items-center justify-center gap-2">
                        <ShieldCheck size={14} className="text-emerald-500" /> Ambiente 100% Seguro
                    </p>
                    <div className="flex justify-center gap-6 opacity-30 grayscale">
                        <img src="https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg" alt="PayPal" className="h-4" />
                        <img src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_logo%2C_revised_2016.svg" alt="Stripe" className="h-4" />
                        <img src="https://logospng.org/download/mercado-pago/logo-mercado-pago-2048.png" alt="Mercado Pago" className="h-4" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RegisterPage;
